import { jwtDecode } from "jwt-decode";

// Refresh this many seconds before the access token actually expires.
const REFRESH_MARGIN_SECONDS = 120;
// setTimeout delay is a 32-bit signed int under the hood; clamp so long-lived
// tokens don't overflow into an immediate fire.
const MAX_TIMEOUT_MS = 2_147_000_000;

let refreshTimer = null;
let refreshPromise = null;

const stripQuotes = (value) => (typeof value === "string" ? value.replace(/"/g, "") : value);

export const getStoredAccessToken = () => {
  const raw = localStorage.getItem("accessToken");
  return raw ? stripQuotes(raw) : null;
};

export const getStoredRefreshToken = () => {
  const raw = localStorage.getItem("refreshToken");
  return raw ? stripQuotes(raw) : null;
};

const decodeExpiry = (token) => {
  if (!token) return null;
  try {
    return jwtDecode(token)?.exp ?? null;
  } catch {
    return null;
  }
};

export const isTokenExpired = (token = getStoredAccessToken()) => {
  const exp = decodeExpiry(token);
  if (!exp) return true;
  return exp - Math.floor(Date.now() / 1000) <= 0;
};

export const isTokenAboutToExpire = (token = getStoredAccessToken()) => {
  const exp = decodeExpiry(token);
  if (!exp) return false;
  const secondsRemaining = exp - Math.floor(Date.now() / 1000);
  return secondsRemaining > 0 && secondsRemaining <= REFRESH_MARGIN_SECONDS;
};

const storeTokens = (accessToken, refreshToken) => {
  localStorage.setItem("accessToken", JSON.stringify(accessToken));
  localStorage.setItem("refreshToken", JSON.stringify(refreshToken));
};

export const clearScheduledRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  refreshPromise = null;
};

/**
 * Refreshes the access token. Concurrent callers share the same in-flight
 * request instead of each firing their own POST /token/refresh.
 */
export const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  const accessToken = getStoredAccessToken();
  const refreshToken = getStoredRefreshToken();

  if (!accessToken || !refreshToken) {
    return Promise.reject(new Error("No tokens available to refresh"));
  }

  refreshPromise = fetch(`${process.env.REACT_APP_API_URL}token/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ accessToken, refreshToken }),
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Token refresh failed with status ${response.status}`);
      }
      const data = await response.json();
      if (!data?.accessToken || !data?.refreshToken) {
        throw new Error("Malformed token refresh response");
      }
      storeTokens(data.accessToken, data.refreshToken);
      scheduleTokenRefresh();
      return data.accessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

/**
 * Ensures the token is fresh before it's needed. Safe to call from anywhere
 * (response interceptors, header builders) - it's a no-op unless the token
 * is actually close to expiring, and dedupes against any refresh already
 * scheduled or in flight.
 */
export const ensureFreshToken = () => {
  const accessToken = getStoredAccessToken();
  if (isTokenAboutToExpire(accessToken)) {
    return refreshAccessToken();
  }
  return Promise.resolve(accessToken);
};

/**
 * Interceptors run outside the React tree, so there's no router `history`
 * to push through - the redirect has to be a raw browser navigation. The
 * app is served under process.env.PUBLIC_URL (e.g. "/app"), so that prefix
 * must be included explicitly or the browser lands on the domain root.
 */
export const handleLogout = () => {
  clearScheduledRefresh();
  localStorage.clear();
  const basePath = process.env.PUBLIC_URL || "";
  window.location.href = `${basePath}/login`;
};

/**
 * Schedules a proactive refresh ~2 minutes before the current access token
 * expires, so the token is renewed in the background even if the app is
 * idle and making no API calls. Call after login and on app boot.
 */
export const scheduleTokenRefresh = () => {
  clearScheduledRefresh();

  const accessToken = getStoredAccessToken();
  const exp = decodeExpiry(accessToken);
  if (!exp) return;

  const secondsUntilExpiry = exp - Math.floor(Date.now() / 1000);
  const msUntilRefresh = Math.min(
    Math.max((secondsUntilExpiry - REFRESH_MARGIN_SECONDS) * 1000, 0),
    MAX_TIMEOUT_MS
  );

  refreshTimer = setTimeout(() => {
    refreshAccessToken().catch(() => {
      // A failed request will hit the response interceptor's 401 handling
      // and log the user out; nothing else to do here.
    });
  }, msUntilRefresh);
};
